<template>
    <pc-page-layout
        id="LoginPageLayout"
        ref="layout"
        v-bind:programs="Programs"
        v-bind:show-program-selector="ShowProgramSelector"
        v-bind:loading="loading">
        <template v-if="!loading">
            <h1
                id="LoginHeader"
                class="legacy-header d-none d-md-block">
                {{ powerclerkHeader }}
            </h1>
            <div class="row justify-content-center">
                <div class="col-md-5 col-lg-3 justify-content-center order-md-2 mb-4 mx-2">
                    <form
                        v-if="!showRedirectLink"
                        id="LoginForm"
                        class="bg-light p-1"
                        v-on:submit.prevent="onSubmit">
                        <div class="m-3">
                            <h3 class="text-center">
                                {{ loginText }}
                            </h3>
                            <div v-if="showStandardLogin">
                                <pc-input
                                    id="UserName"
                                    key="UserName"
                                    v-model="username"
                                    v-bind:placeholder="usernamePlaceholder"
                                    v-bind:label="usernameInputLabel"
                                    group-id="LoginForm"
                                    autocomplete="username"
                                    inputmode="email"
                                    class="mb-3" />
                                <pc-input
                                    id="Password"
                                    v-model="password"
                                    v-bind:label="passwordInputLabel"
                                    type="password"
                                    group-id="LoginForm"
                                    autocomplete="current-password"
                                    class="mb-3" />
                                <div class="w-100 mb-3">
                                    <pc-loader
                                        v-bind:loading="processing"
                                        small>
                                        <pc-button
                                            id="LoginButton"
                                            primary
                                            class="w-100"
                                            data-gtmid="Login-login">
                                            {{ loginBtnLabel }}
                                        </pc-button>
                                    </pc-loader>
                                </div>
                                <div class="text-center">
                                    <a
                                        id="ForgotPasswordHyperLink"
                                        v-bind:href="Model.ForgotPasswordUrl">
                                        {{ forgotPasswordLinkText }}
                                    </a>
                                </div>
                                <div
                                    v-if="Model.AllowsSelfRegistration"
                                    class="text-center">
                                    <a v-bind:href="Model.SelfRegisterUrl">{{ selfRegisterLinkText }}</a>
                                    <pc-temp-space />
                                    <span>{{ selfRegisterText2 }}</span>
                                </div>
                            </div>
                            <div v-if="showSsoLogin">
                                <div
                                    v-for="(idp, index) in Model.IdentityProviders"
                                    v-bind:key="index"
                                    class="d-flex align-items-center">
                                    <a
                                        v-bind:href="idp.IdpHref"
                                        v-bind:data-test-role="idp.IdpPublicName"
                                        class="flex-grow-1 mt-3 mb-2 text-center text-md-center">
                                        {{ idp.IdpLinkText }}
                                    </a>
                                </div>
                            </div>
                            <div v-if="showMfaLogin">
                                <pc-input
                                    id="MfaCode"
                                    key="MfaCode"
                                    ref="mfaCodeInput"
                                    v-model="mfaCode"
                                    v-bind:label="mfaInputLabel"
                                    autocomplete="one-time-code"
                                    inputmode="numeric"
                                    class="mb-3" />
                                <div class="w-100 mb-3">
                                    <pc-loader
                                        v-bind:loading="processing"
                                        small>
                                        <pc-button
                                            id="MfaCodeButton"
                                            primary
                                            class="w-100">
                                            {{ verifyMfaCodeBtnLabel }}
                                        </pc-button>
                                    </pc-loader>
                                </div>
                                <div class="d-flex align-items-center justify-content-center mb-3">
                                    <pc-button
                                        type="button"
                                        class="p-0"
                                        link
                                        v-on:click="showMfaHelp = !showMfaHelp">
                                        {{ mfaHelpLinkLabel }}
                                    </pc-button>
                                </div>
                                <transition
                                    v-bind:css="false"
                                    v-on:enter="enter"
                                    v-on:leave="leave">
                                    <div
                                        v-show="showMfaHelp"
                                        class="mb-3">
                                        {{ Model.MfaSupportText }}
                                    </div>
                                </transition>
                            </div>
                            <pc-alert
                                v-bind="alertBinder"
                                v-model:shown="showAlert"
                                class="mt-3" />
                            <div
                                v-if="showResendConfirmationButton"
                                class="d-flex align-items-center justify-content-center mb-3">
                                <pc-button
                                    type="button"
                                    class="p-0"
                                    link
                                    v-on:click="resendConfirmation">
                                    {{ resendConfirmationLinkText }}
                                </pc-button>
                            </div>
                        </div>
                    </form>
                    <pc-alert
                        v-if="showRedirectLink"
                        id="RedirectMessage"
                        severity="error"
                        shown>
                        <div class="flex-column">
                            <i18n-t
                                v-if="showDeactivatedUserText"
                                keypath="login.deactivatedUserRedirect"
                                tag="div">
                                <template v-slot:redirectLinkText>
                                    <a
                                        id="AccountSettingRedirect"
                                        v-bind:href="redirectAddress">
                                        {{ clickHereText }}
                                    </a>
                                </template>
                            </i18n-t>
                            <i18n-t
                                v-if="showDifferentProgramText"
                                keypath="login.differentProgramRedirect"
                                tag="div">
                                <template v-slot:redirectLinkText>
                                    <a
                                        id="OldProgramRedirect"
                                        v-bind:href="redirectAddress">
                                        {{ redirectProgramName }}
                                    </a>
                                </template>
                            </i18n-t>
                            <br />
                            <pc-button
                                data-test-role="use-different-login-btn"
                                v-on:click="reattemptLogin">
                                {{ retryLoginButtonText }}
                            </pc-button>
                        </div>
                    </pc-alert>
                </div>
                <div class="col justify-content-center mx-2">
                    <div
                        v-if="hasFrontPageContent"
                        class="iframe-wrapper">
                        <iframe
                            id="MainPanelFrame"
                            ref="frontPageContent"
                            allowfullscreen="allowfullscreen"
                            v-bind:src="Model.FrontPageSrc"
                            v-bind:height="frontPageHeight"
                            class="w-100"
                            title="Program Front Page Content"
                            frameborder="0"
                            marginwidth="0"
                            marginheight="0"
                            scrolling="auto"
                            onload="onFrontPageLoaded(this)" />
                    </div>
                </div>
            </div>
        </template>
    </pc-page-layout>
</template>
<script>
    import pageBase from "Components/page/_page-base.js";
    import { LOGIN_ERROR_TYPE } from "Anonymous/loginStatusCode";
    import { ALERT_SEVERITY, ALERT_DURATION } from "Util/alerts";
    import { SLIDE_DOWN_UP } from "Util/animations";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/login.i18n";
    import { htmlUnescape } from "Util/util";
    import { responsiveManager } from "Responsive/manager";

    if (window) {
        // Expose a global function so that the front page content iframe
        // has a global function to attach as a handler for its onload event.
        // This will be replaced by a bound method of the login Vue component
        // that properly handles the onload event, so this empty function will
        // never be executed
        window.onFrontPageLoaded = () => {
            throw new Error("Failed to replace global onFrontPageLoaded function.");
        };
    }

    export default {
        name: "Login",
        extends: pageBase,
        setup: pageBase.setup,
        data() {
            return {
                username: "",
                password: "",
                previousUsername: "",
                mfaCode: "",
                frontPageHeight: null,
                hasInitializedFrontPageContent: false,
                processing: false,
                confirmationResending: false,
                showAlert: false,
                loginStatusCode: LOGIN_ERROR_TYPE.None,
                alertMessage: null,
                alertSeverity: null,
                showMfaHelp: null,
                redirectProgramName: null,
                isDeactivatedUser: false,
                redirectAddress: null
            };
        },
        computed: {
            showResendConfirmationButton() {
                return this.loginStatusCode === LOGIN_ERROR_TYPE.Unconfirmed && !this.usernameChanged;
            },
            usernameChanged() {
                return this.username !== this.previousUsername;
            },
            hasFrontPageContent() {
                return this.Model.FrontPageSrc;
            },
            showStandardLogin() {
                return (!this.showSsoLogin || this.Model.AllowLocalLogin) && !this.showMfaLogin;
            },
            showSsoLogin() {
                return this.Model.IdentityProviders && this.Model.IdentityProviders.length > 0 && !this.showMfaLogin;
            },
            showMfaLogin() {
                return this.loginStatusCode === LOGIN_ERROR_TYPE.MfaRequired;
            },
            showRedirectLink() {
                return this.loginStatusCode === LOGIN_ERROR_TYPE.DifferentProgramFound;
            },
            showDeactivatedUserText() {
                return this.showRedirectLink && this.isDeactivatedUser;
            },
            showDifferentProgramText() {
                return this.showRedirectLink && !this.showDeactivatedUserText;
            },
            loginText() {
                return this.showMfaLogin ? this.mfaPaneHeader : this.loginPaneHeader;
            },
            loginErrorMessage() {
                return this._getMessageForErrorType(this.loginStatusCode);
            },
            // Add a little extra vertical space so vertical scrollbars
            // don't start to appear as content approaches the vertical limits
            // of the iframe container. If scaling down to the xs viewport size,
            // after the initial load, decreasing the width further below the xs
            // breakpoint causes considerably more wrapping than at larger
            // viewport sizes, so add a larger vertical buffer. Don't add the
            // larger vertical buffer if this is still the initial page load, since
            // that likely means the page is being loaded on a smaller viewport
            // (eg. vertical phone) and the width is unlikely to decrease further.
            frontPageContentBufferHeight() {
                if (this.hasInitializedFrontPageContent && responsiveManager.breakpoint.value == "xs") {
                    return 600;
                } else {
                    return 100;
                }
            },
            LOGIN_ERROR_MESSAGES() {
                return {
                    [LOGIN_ERROR_TYPE.None]: "",
                    [LOGIN_ERROR_TYPE.Unconfirmed]: this.loginErrorUnconfirmed,
                    [LOGIN_ERROR_TYPE.LockedOut]: this.loginErrorLockedOut,
                    [LOGIN_ERROR_TYPE.IncorrectCredentials]: this.loginErrorIncorrectCredentials,
                    [LOGIN_ERROR_TYPE.NoMappedPrograms]: this.loginErrorNoMappedPrograms,
                    [LOGIN_ERROR_TYPE.OtherError]: this.loginErrorOtherError,
                    [LOGIN_ERROR_TYPE.MfaRequired]: "",
                    [LOGIN_ERROR_TYPE.MfaCodeInvalid]: this.loginErrorMfaCodeInvalid,
                    [LOGIN_ERROR_TYPE.MfaSecretLocked]: this.loginErrorMfaSecretLocked,
                    [LOGIN_ERROR_TYPE.SsoRequiredForUserInProgram]: this.loginErrorSsoRequiredForUserInProgram,
                    [LOGIN_ERROR_TYPE.DifferentProgramFound]: "",
                    [LOGIN_ERROR_TYPE.SelfRegistrationRequired]: ""
                };
            },
            alertBinder() {
                return {
                    message: this.alertMessage,
                    severity: this.alertSeverity,
                    duration: ALERT_DURATION.long,
                    textId: this.alertSeverity == ALERT_SEVERITY.Error ? "LoginErrorAlert" : "LoginSuccessAlert"
                };
            },
            breakpoint() {
                return responsiveManager.breakpoint.value;
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            async breakpoint() {
                // If the viewport changes size, force the iframe holding the
                // front page content to reload by setting its src to empty and
                // then back to the original src. This will trigger onFrontPageLoaded
                // which will properly size the reloaded iframe for the new viewport
                // size
                const src = this.Model.FrontPageSrc;
                this.Model.FrontPageSrc = "";
                // Set to 0 so when the content reloads the scrollheight of the iframe
                // content will accurately reflect the content's length
                this.frontPageHeight = 0;
                await this.$nextTick();
                this.Model.FrontPageSrc = src;
            },
            loginStatusCode() {
                this.previousUsername = this.username;
            }
        },
        created() {
            // The frontpage content iframe is bound to a global onFrontPageLoaded
            // function defined in setupAnonEntrypoint.js, but we actually want to
            // call this component's local resize method when the iframe content is
            // loaded. As far as I could discover, the only way to trigger a callback
            // when the iframe's content was loaded was via it's onload attribute, but
            // that needs a globally available function.
            window.onFrontPageLoaded = this.onFrontPageLoaded;
        },
        methods: {
            async logUserIn() {
                // Prevent double-clicks of login button
                if (this.processing) {
                    return;
                }
                try {
                    this.processing = true;
                    this.redirectProgramName = null;
                    this.isDeactivatedUser = false;
                    this.loginStatusCode = null;
                    let resultData = await this.LogUserIn({
                        userEmail: this.username,
                        password: this.password,
                        programId: this.$app.ProgramId
                    });
                    if (resultData.Data) {
                        resultData = resultData.Data;
                    }
                    this.processing = false;
                    this.loginStatusCode = resultData.errorType;
                    this.redirectAddress = resultData.redirectAddress;
                    if (this.loginStatusCode == LOGIN_ERROR_TYPE.None) {
                        this._redirectOnSuccess(resultData.redirectAddress);
                    } else if (this.loginStatusCode == LOGIN_ERROR_TYPE.SelfRegistrationRequired) {
                        this.$app.setHref(resultData.redirectAddress);
                    } else if (this.showRedirectLink) {
                        this.redirectProgramName = resultData.redirectProgramName;
                        this.isDeactivatedUser = resultData.isDeactivatedUser;
                    } else if (this.loginStatusCode != LOGIN_ERROR_TYPE.MfaRequired) {
                        this._showErrorAlert(this.loginErrorMessage);
                    } else {
                        // Wait for MFA input to be displayed and focus on it
                        await this.$nextTick();
                        if (this.$refs.mfaCodeInput) {
                            this.$refs.mfaCodeInput.focusIn();
                        }
                    }
                } catch (e) {
                    this._showErrorAlert(e);
                } finally {
                    this.processing = false;
                }
            },
            async logMfaUserIn() {
                // Prevent double-clicks of login button
                if (this.processing) {
                    return;
                }
                try {
                    this.processing = true;
                    this.redirectProgramName = null;
                    this.isDeactivatedUser = false;
                    this.loginStatusCode = null;
                    let resultData = await this.LogMfaUserIn({
                        userEmail: this.username,
                        password: this.password,
                        programId: this.$app.ProgramId,
                        totp: this.mfaCode
                    });
                    if (resultData.Data) {
                        resultData = resultData.Data;
                    }
                    this.processing = false;
                    this.loginStatusCode = resultData.errorType;
                    this.redirectAddress = resultData.redirectAddress;
                    if (resultData.errorType === LOGIN_ERROR_TYPE.None) {
                        this._redirectOnSuccess(resultData.redirectAddress);
                    } else if (this.loginStatusCode == LOGIN_ERROR_TYPE.SelfRegistrationRequired) {
                        this.$app.setHref(resultData.redirectAddress);
                    } else if (this.showRedirectLink) {
                        this.redirectProgramName = resultData.redirectProgramName;
                        this.isDeactivatedUser = resultData.isDeactivatedUser;
                    } else {
                        this._showErrorAlert(this.loginErrorMessage);
                        this.password = "";
                        this.mfaCode = "";
                    }
                } catch (e) {
                    this._showErrorAlert(e);
                } finally {
                    this.processing = false;
                }
            },
            async onFrontPageLoaded() {
                // Set the height of the front page content iFrame based on the
                // height of the contents, plus a buffer to account for wrapping
                // as the window is scaled down within a single responsive breakpoint.
                const iframe = document.getElementById("MainPanelFrame");
                // Give the iFrame a moment to render its content before measuring height
                await this.$nextTick();

                const contentDoc = iframe?.contentDocument;
                if (contentDoc?.body) {
                    this.frontPageHeight = contentDoc.body.scrollHeight + this.frontPageContentBufferHeight;
                    this.hasInitializedFrontPageContent = true;
                }
            },
            async resendConfirmation() {
                if (this.confirmationResending) {
                    return;
                }
                this.showAlert = false;
                this.confirmationResending = true;
                try {
                    const result = await this.ResendConfirmation({
                        userEmail: this.username,
                        programId: this.ProgramId
                    });
                    if (result.status == "success") {
                        this.loginStatusCode = LOGIN_ERROR_TYPE.None;
                        this._showSuccessAlert(this.confirmationResentAlertText);
                    }
                } finally {
                    this.confirmationResending = false;
                }
            },
            _showErrorAlert(message) {
                this.alertSeverity = ALERT_SEVERITY.error;
                this.alertMessage = message;
                this.showAlert = true;
            },
            _showSuccessAlert(message) {
                this.alertSeverity = ALERT_SEVERITY.success;
                this.alertMessage = message;
                this.showAlert = true;
            },
            _redirectOnSuccess(redirectAddress) {
                if (this.Model.RedirectUrl) {
                    redirectAddress = htmlUnescape(this.Model.RedirectUrl);
                }
                this.$app.replaceHref(redirectAddress);
            },
            navigateToSsoProvider(href) {
                this.$app.replaceHref(href);
            },
            enter: SLIDE_DOWN_UP.enter,
            leave: SLIDE_DOWN_UP.leave,
            onSubmit() {
                if (this.showStandardLogin) {
                    this.logUserIn();
                } else if (this.showMfaLogin) {
                    this.logMfaUserIn();
                }
            },
            _getMessageForErrorType(errorType) {
                return this.LOGIN_ERROR_MESSAGES[errorType];
            },
            reattemptLogin() {
                this.redirectProgramName = null;
                this.isDeactivatedUser = false;
                this.redirectAddress = null;
                this.showAlert = false;
                this.loginStatusCode = LOGIN_ERROR_TYPE.None;
            }
        }
    };
</script>
<style lang="scss" scoped>
    #LoginHeader {
        border-bottom: 1px solid var(--bs-gray-400) !important;
    }

    .iframe-wrapper {
        -webkit-overflow-scrolling: touch;
        overflow-x: auto;
    }
</style>
